import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Provence.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;

const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

const fmtDate = raw => {
  if (!raw) return 'ДД · ММ · ГГГГ';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join(' · ');
};

/* ── Scroll-reveal hook ──────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.prov-reveal, .prov-reveal-left, .prov-reveal-right, .prov-reveal-bloom';

    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('prov-in'));
      return;
    }

    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('prov-in');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ── SVG Decorations ─────────────────────────────────────────── */
function FloralWave({ color = '#9b7fb6', opacity = 0.6 }) {
  return (
    <svg className="prov-floral-wave" viewBox="0 0 400 40" preserveAspectRatio="none" aria-hidden="true">
      <path
        d="M0,20 Q25,5 50,20 Q75,35 100,20 Q125,5 150,20 Q175,35 200,20 Q225,5 250,20 Q275,35 300,20 Q325,5 350,20 Q375,35 400,20"
        stroke={color}
        fill="none"
        strokeWidth="1.5"
        opacity={opacity}
      />
    </svg>
  );
}

function FloralWreath({ accent, rose }) {
  return (
    <svg className="prov-wreath" viewBox="0 0 220 100" aria-hidden="true">
      {/* Left branch */}
      <path d="M20,50 Q40,20 65,35 Q50,50 65,65 Q40,80 20,50" stroke={accent} fill="none" strokeWidth="1" opacity="0.5"/>
      <circle cx="40" cy="28" r="5" fill={rose} opacity="0.4"/>
      <circle cx="55" cy="20" r="4" fill={rose} opacity="0.35"/>
      <ellipse cx="30" cy="50" rx="6" ry="4" fill="#7a9e7e" opacity="0.35" transform="rotate(-30 30 50)"/>
      <ellipse cx="45" cy="65" rx="5" ry="3" fill="#7a9e7e" opacity="0.3" transform="rotate(20 45 65)"/>
      {/* Right branch */}
      <path d="M200,50 Q180,20 155,35 Q170,50 155,65 Q180,80 200,50" stroke={accent} fill="none" strokeWidth="1" opacity="0.5"/>
      <circle cx="180" cy="28" r="5" fill={rose} opacity="0.4"/>
      <circle cx="165" cy="20" r="4" fill={rose} opacity="0.35"/>
      <ellipse cx="190" cy="50" rx="6" ry="4" fill="#7a9e7e" opacity="0.35" transform="rotate(30 190 50)"/>
      <ellipse cx="175" cy="65" rx="5" ry="3" fill="#7a9e7e" opacity="0.3" transform="rotate(-20 175 65)"/>
      {/* Top flowers */}
      <circle cx="110" cy="12" r="7" fill={rose} opacity="0.45"/>
      <circle cx="110" cy="12" r="3" fill="#fff" opacity="0.6"/>
      <circle cx="92" cy="18" r="5" fill={rose} opacity="0.35"/>
      <circle cx="128" cy="18" r="5" fill={rose} opacity="0.35"/>
      {/* Connecting arc */}
      <path d="M65,35 Q110,5 155,35" stroke={accent} fill="none" strokeWidth="1" opacity="0.4"/>
    </svg>
  );
}

function HeroWave() {
  return (
    <div className="prov-hero-wave" aria-hidden="true">
      <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
        <path d="M0,40 Q180,80 360,40 Q540,0 720,40 Q900,80 1080,40 Q1260,0 1440,40 L1440,80 L0,80 Z"
          fill="#f7f3ee" />
      </svg>
    </div>
  );
}

/* ── Countdown ───────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({
        d: Math.floor(ms / 86400000),
        h: Math.floor(ms % 86400000 / 3600000),
        m: Math.floor(ms % 3600000 / 60000),
        s: Math.floor(ms % 60000 / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);

  return (
    <div className="prov-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="prov-cu">
          <div className="prov-cu-card">
            <span className="prov-cu-val" style={{ color: accent }}>{String(v).padStart(2, '0')}</span>
          </div>
          <span className="prov-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── Calendar ────────────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++)    cells.push({ n: i, cur: true });
  while (cells.length < 42)         cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

  return (
    <div className="prov-calendar">
      <div className="prov-cal-header">
        <span style={{ color: accent }}>{MON[mo]}</span>
        <span className="prov-cal-year">{yr}</span>
      </div>
      <div className="prov-cal-grid">
        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(h => (
          <div key={h} className="prov-cal-head" style={{ color: accent }}>{h}</div>
        ))}
        {cells.map((c, i) => {
          const isWed = c.cur && c.n === day;
          return (
            <div key={i}
              className={`prov-cal-cell${!c.cur ? ' prov-cal-muted' : ''}${isWed ? ' prov-cal-today' : ''}`}
              style={isWed ? { background: accent, color: '#fff' } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVP Form ───────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status, setStatus] = useState('confirmed');
  const [name, setName] = useState('');
  const [survey, setSurvey] = useState({});
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="prov-rsvp-done" style={{ borderColor: accent }}>
      <div className="prov-rsvp-done-icon">🌿</div>
      <strong style={{ color: accent }}>Merci!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас с нетерпением!' : 'Жаль, что не сможете прийти.'}</p>
    </div>
  );

  return (
    <form className="prov-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="prov-rsvp-options">
        {[
          { v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' },
          { v: 'declined',  l: bc.rsvp?.noText  || 'Не смогу прийти' },
        ].map(o => (
          <label key={o.v}
            className={`prov-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { background: accent, color: '#fff', borderColor: accent } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <div className="prov-input-wrap">
        <input className="prov-input" placeholder="Ваше имя"
          value={name} onChange={e => setName(e.target.value)} required />
      </div>

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="prov-survey-q">
          <p className="prov-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="prov-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="prov-survey-opts">{(q.options || []).map(opt => {
                const sel = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`prov-rsvp-opt prov-rsvp-opt-sm${sel ? ' active' : ''}`}
                    style={sel ? { background: accent, color: '#fff', borderColor: accent } : {}}
                  >
                    <input type="checkbox" name={q.id} value={opt} checked={sel}
                      onChange={e => {
                        const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                        setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                      }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="prov-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`prov-rsvp-opt prov-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { background: accent, color: '#fff', borderColor: accent } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div className="prov-input-wrap">
          {bc.message?.fieldLabel && (
            <p className="prov-field-label">{bc.message.fieldLabel}</p>
          )}
          <textarea className="prov-input" rows={4}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}

      <button type="submit" className="prov-btn" style={{ background: accent }}>
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ────────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="prov-wishlist">
      {items.map(item => (
        <div key={item.id} className="prov-wish-item prov-reveal-bloom">
          <div className="prov-wish-info">
            <div className="prov-wish-name">{item.name}</div>
            {item.price && <div className="prov-wish-price" style={{ color: accent }}>{item.price} ₽</div>}
          </div>
          <div className="prov-wish-actions">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="prov-btn-outline"
                style={{ borderColor: accent, color: accent }}>
                Посмотреть
              </a>
            )}
            {reserved[item.id]
              ? <button type="button" className="prov-btn prov-btn-muted"
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  ✓ Снять резерв
                </button>
              : <button type="button" className="prov-btn" style={{ background: accent }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Section wrapper ─────────────────────────────────────────── */
function ProvSection({ id, title, subtitle, accent, children, className = '' }) {
  return (
    <section className={`prov-section ${className}`} id={id}>
      <div className="prov-section-head prov-reveal">
        <h2 className="prov-section-title" style={{ borderLeftColor: accent }}>{title}</h2>
        <BlockSubtitle text={subtitle} className="prov-section-sub" />
        <FloralWave color={accent} opacity={0.4} />
      </div>
      {children}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
export default function Provence({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks  = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc     = project.block_content || {};
  const accent = project.palette?.primary || '#9b7fb6';
  const rose   = '#d4829a';

  const fontTitle = project.fonts?.title || null;
  const fontBody  = project.fonts?.body  || null;
  const fontStyle = {
    ...(fontTitle ? { '--f-title': `'${fontTitle}', 'Lora', serif`        } : {}),
    ...(fontBody  ? { '--f-body':  `'${fontBody}',  'Nunito', sans-serif` } : {}),
  };

  /* Dynamic Google Fonts */
  useEffect(() => {
    const id = 'prov-gfonts';
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id  = id;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const families = [fontTitle, fontBody].filter(Boolean);
    const baseQuery = "family=Lora:ital,wght@0,400;0,600;1,400;1,600&family=Nunito:wght@300;400;600;700";
    const extraQuery = families.map(f => `family=${encodeURIComponent(f)}:wght@400;500;600;700`).join('&');
    link.href = `https://fonts.googleapis.com/css2?${baseQuery}${extraQuery ? '&' + extraQuery : ''}&display=swap`;
  }, [fontTitle, fontBody]);

  /* Photos */
  const photos    = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const p1        = photos[0];
  const p2        = photos[1];
  const invPhotos = project.invitation_photos || [];
  const invP1     = invPhotos[0] || photos[0] || null;
  const invP2     = invPhotos[1] || photos[1] || null;
  const invP3     = invPhotos[2] || photos[2] || null;
  const dcPhotos  = project.dresscode_photos || [];

  const bride  = project.bride || 'Невеста';
  const groom  = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');
  const HAS_CALENDAR  = blocks.includes('calendar');
  const HAS_NAV       = blocks.includes('menu');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  /* Google Calendar helper */
  const calAddBtn = HAS_CALENDAR ? (() => {
    const d   = new Date(weddingDate);
    const pad = n => String(n).padStart(2, '0');
    const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const title = encodeURIComponent(`Свадьба ${bride} & ${groom}`);
    const href  = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer"
        className="prov-cal-add-btn" style={{ background: accent }}>
        🌿 {bc.calendar?.title || 'Добавить в календарь'}
      </a>
    );
  })() : null;

  return (
    <div
      className="prov-page"
      ref={pageRef}
      style={{ '--prov-accent': accent, '--prov-rose': rose, ...fontStyle }}
      data-container="provence"
    >
      {/* ══ NAV ══════════════════════════════════════════════════ */}
      {HAS_NAV && (
        <nav className="prov-nav">
          <div className="prov-nav-inner">
            <span className="prov-nav-brand">{bride} &amp; {groom}</span>
            <div className="prov-nav-links">
              {HAS_SCHEDULE  && <a href="#schedule">Программа</a>}
              {HAS_MAP       && <a href="#map">Локация</a>}
              {HAS_DRESSCODE && <a href="#dresscode">Дресс-код</a>}
              {HAS_RSVP      && (
                <a href="#rsvp" className="prov-nav-cta" style={{ background: accent, color: '#fff' }}>
                  Анкета
                </a>
              )}
            </div>
          </div>
        </nav>
      )}

      {/* ══ HERO ══════════════════════════════════════════════════ */}
      <section className="prov-hero">
        {/* Background image(s) */}
        {p1 && (
          <div className="prov-hero-photo">
            <img src={src(p1.url)} alt="" style={{ filter: flt(p1), objectPosition: objPos(p1) }} />
          </div>
        )}
        <div className="prov-hero-overlay" />

        <div className="prov-hero-content">
          <FloralWreath accent={accent} rose={rose} />

          <div className="prov-hero-names">
            <h1 className="prov-hero-name-bride">{bride}</h1>
            <div className="prov-hero-et">
              <span className="prov-hero-et-line" style={{ background: accent }} />
              <span className="prov-hero-et-word" style={{ color: accent }}>et</span>
              <span className="prov-hero-et-line" style={{ background: accent }} />
            </div>
            <h1 className="prov-hero-name-groom">{groom}</h1>
          </div>

          <p className="prov-hero-slogan">
            {bc.hero?.subtitle || project.wedding_slogan || 'Вы приглашены'}
          </p>

          <div className="prov-hero-date">
            {fmtDate(weddingDate)}
            {project.wedding_time && <span className="prov-hero-time"> · {project.wedding_time}</span>}
          </div>

          {HAS_RSVP && (
            <a href="#rsvp" className="prov-hero-btn" style={{ background: accent }}>
              {bc.rsvp?.yesText || 'Заполнить анкету'}
            </a>
          )}
        </div>

        <HeroWave />
      </section>

      {/* ══ INVITATION ════════════════════════════════════════════ */}
      <section className="prov-inv-section" id="invitation">
        <div className="prov-inv-inner">
          {/* Text card */}
          <div className="prov-inv-card prov-reveal-bloom">
            <div className="prov-inv-corner prov-inv-corner--tl" aria-hidden="true">
              <svg viewBox="0 0 30 30" width="30" height="30">
                <circle cx="15" cy="15" r="6" fill={rose} opacity="0.5"/>
                <circle cx="15" cy="15" r="3" fill={rose} opacity="0.7"/>
                <path d="M5,15 Q10,5 15,10" stroke={accent} fill="none" strokeWidth="1" opacity="0.5"/>
                <path d="M15,5 Q20,10 15,15" stroke={accent} fill="none" strokeWidth="1" opacity="0.5"/>
              </svg>
            </div>
            <h2 className="prov-inv-title" style={{ color: accent }}>
              {bc.message?.title || 'Дорогие гости'}
            </h2>
            <div className="prov-inv-text">
              {bc.message?.text
                ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
                : (
                  <>
                    <p>Avec toute notre joie — с великой радостью мы приглашаем вас разделить с нами <strong>самый прекрасный день</strong> нашей жизни.</p>
                    <p>Среди лавандовых полей и тёплого солнца мы хотим отпраздновать наше бракосочетание в окружении самых близких людей.</p>
                    <p>Пожалуйста, подтвердите участие <strong style={{ color: accent }}>через форму</strong> на сайте.</p>
                  </>
                )
              }
            </div>
            {HAS_RSVP && (
              <a href="#rsvp" className="prov-btn" style={{ background: accent }}>
                {bc.rsvp?.btnText || 'Заполнить анкету'}
              </a>
            )}
          </div>

          {/* Photos mosaic */}
          <div className="prov-inv-photos prov-reveal-right">
            <div className="prov-inv-photos-inner">
              {invP1 ? (
                <div className="prov-inv-photo prov-inv-photo--main">
                  <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1) }} />
                </div>
              ) : (
                <div className="prov-inv-photo prov-inv-photo--main prov-inv-photo--empty">
                  <span>Ваше фото</span>
                </div>
              )}
              <div className="prov-inv-photos-side">
                {invP2 ? (
                  <div className="prov-inv-photo prov-inv-photo--side">
                    <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2) }} />
                  </div>
                ) : (
                  <div className="prov-inv-photo prov-inv-photo--side prov-inv-photo--empty">
                    <span>Фото 2</span>
                  </div>
                )}
                {invP3 ? (
                  <div className="prov-inv-photo prov-inv-photo--side">
                    <img src={src(invP3.url)} alt="" style={{ filter: flt(invP3) }} />
                  </div>
                ) : (
                  <div className="prov-inv-photo prov-inv-photo--side prov-inv-photo--empty">
                    <span>Фото 3</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="prov-inv-calendar prov-reveal">
          <CalendarWidget date={weddingDate} accent={accent} addBtn={calAddBtn} />
        </div>
      </section>

      <div className="prov-divider"><FloralWave color={accent} opacity={0.35} /></div>

      {/* ══ COUNTDOWN ═════════════════════════════════════════════ */}
      {HAS_COUNTDOWN && (
        <>
          <ProvSection
            id="countdown"
            title={bc.countdown?.title || 'До торжества'}
            subtitle={bc.countdown?.subtitle}
            accent={accent}
          >
            <div className="prov-reveal" style={{ '--d': '0.15s' }}>
              <Countdown date={weddingDate} accent={accent} />
            </div>
          </ProvSection>
          <div className="prov-divider"><FloralWave color={accent} opacity={0.35} /></div>
        </>
      )}

      {/* ══ SCHEDULE ══════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <>
          <ProvSection
            id="schedule"
            title={bc.schedule?.title || 'Программа дня'}
            subtitle={bc.schedule?.subtitle}
            accent={accent}
          >
            <div className="prov-schedule">
              {allItems.length > 0 ? (
                <>
                  {optional.length > 0 && (
                    <div className="prov-sched-group">
                      <div className="prov-sched-group-title" style={{ color: accent }}>Необязательная часть</div>
                      {optional.map((item, i) => (
                        <div key={i} className="prov-sched-item prov-reveal-bloom" style={{ '--d': `${i * 0.08}s` }}>
                          <div className="prov-sched-time-col">
                            <span className="prov-sched-time" style={{ color: accent }}>{item.time}</span>
                          </div>
                          <div className="prov-sched-flower" style={{ color: rose }}>
                            <svg width="16" height="16" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="3" fill="currentColor"/>
                              <circle cx="12" cy="5" r="2.5" fill="currentColor" opacity="0.5"/>
                              <circle cx="12" cy="19" r="2.5" fill="currentColor" opacity="0.5"/>
                              <circle cx="5" cy="12" r="2.5" fill="currentColor" opacity="0.5"/>
                              <circle cx="19" cy="12" r="2.5" fill="currentColor" opacity="0.5"/>
                            </svg>
                          </div>
                          <div className="prov-sched-info">
                            <div className="prov-sched-event">{item.event}</div>
                            {item.description && <div className="prov-sched-desc">{item.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="prov-sched-group">
                    {optional.length > 0 && (
                      <div className="prov-sched-group-title" style={{ color: accent }}>Основная программа</div>
                    )}
                    {mainItems.map((item, i) => (
                      <div key={i} className="prov-sched-item prov-reveal-bloom" style={{ '--d': `${i * 0.08}s` }}>
                        <div className="prov-sched-time-col">
                          <span className="prov-sched-time" style={{ color: accent }}>{item.time}</span>
                        </div>
                        <div className="prov-sched-flower" style={{ color: rose }}>
                          <svg width="16" height="16" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="3" fill="currentColor"/>
                            <circle cx="12" cy="5" r="2.5" fill="currentColor" opacity="0.5"/>
                            <circle cx="12" cy="19" r="2.5" fill="currentColor" opacity="0.5"/>
                            <circle cx="5" cy="12" r="2.5" fill="currentColor" opacity="0.5"/>
                            <circle cx="19" cy="12" r="2.5" fill="currentColor" opacity="0.5"/>
                          </svg>
                        </div>
                        <div className="prov-sched-info">
                          <div className="prov-sched-event">{item.event}</div>
                          {item.description && <div className="prov-sched-desc">{item.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="prov-sched-placeholder">
                  {[['14:00', 'Сбор гостей'], ['15:00', 'Церемония'], ['16:00', 'Фотосессия'], ['17:00', 'Банкет']].map(([t, e], i) => (
                    <div key={i} className="prov-sched-item" style={{ opacity: 0.35 }}>
                      <div className="prov-sched-time-col">
                        <span className="prov-sched-time">{t}</span>
                      </div>
                      <div className="prov-sched-flower">
                        <svg width="14" height="14" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="3" fill={rose} opacity="0.4"/>
                        </svg>
                      </div>
                      <div className="prov-sched-event">{e}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ProvSection>
          <div className="prov-divider"><FloralWave color={accent} opacity={0.35} /></div>
        </>
      )}

      {/* ══ MAP ═══════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <>
          <ProvSection
            id="map"
            title={bc.map?.title || 'Место проведения'}
            subtitle={bc.map?.subtitle}
            accent={accent}
          >
            <div className="prov-venues">
              {venues.length > 0 ? venues.map((v, i) => (
                <div key={i} className="prov-venue prov-reveal-bloom" style={{ '--d': `${i * 0.1}s` }}>
                  <div className="prov-venue-pin" style={{ background: accent + '20', color: accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="prov-venue-info">
                    {v.label && <span className="prov-venue-label" style={{ color: accent }}>{v.label}</span>}
                    {v.name  && <span className="prov-venue-name">{v.name}</span>}
                    <span className="prov-venue-addr">{v.address}</span>
                  </div>
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                    target="_blank" rel="noopener noreferrer"
                    className="prov-btn-outline" style={{ borderColor: accent, color: accent }}>
                    Маршрут →
                  </a>
                </div>
              )) : project.wedding_address ? (
                <div className="prov-venue prov-reveal-bloom">
                  <div className="prov-venue-pin" style={{ background: accent + '20', color: accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    </svg>
                  </div>
                  <span className="prov-venue-addr">{project.wedding_address}</span>
                </div>
              ) : (
                <p className="prov-placeholder-text">Адрес будет добавлен на шаге «Данные»</p>
              )}
            </div>

            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="prov-map-frame prov-reveal" style={{ '--d': '0.2s' }}>
              {(() => {
                if (project.map_url) {
                  return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const pts = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  const cx  = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const cy  = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  return (
                    <MapFrame region={project.region} title="Карта" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }}
                      src={`https://yandex.ru/map-widget/v1/?pt=${pts}&ll=${cx},${cy}&z=14&l=map`} />
                  );
                }
                const addr = venues[0]?.address || project.wedding_address;
                if (addr) {
                  return (
                    <MapFrame region={project.region} title="Карта" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }}
                      src={`https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=15&l=map`} />
                  );
                }
                return (
                  <div className="prov-map-placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill={accent} opacity="0.3">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                    </svg>
                    <span>Карта появится после добавления адреса</span>
                  </div>
                );
              })()}
            </div>
            )}
          </ProvSection>
          <div className="prov-divider"><FloralWave color={accent} opacity={0.35} /></div>
        </>
      )}

      {/* ══ DRESSCODE ═════════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <>
          <ProvSection
            id="dresscode"
            title={bc.palette?.title || 'Дресс-код'}
            subtitle={bc.palette?.subtitle}
            accent={accent}
          >
            {project.dresscode_palette?.length > 0 && (
              <div className="prov-palette prov-reveal">
                {project.dresscode_palette.map((c, i) => (
                  <div key={i} className="prov-swatch-wrap">
                    <div className="prov-swatch" style={{ background: c.hex || c }} />
                    {c.name && <span className="prov-swatch-name">{c.name}</span>}
                  </div>
                ))}
              </div>
            )}
            {dcPhotos.length > 0 && (
              <div className="prov-dc-photos prov-reveal" style={{ '--d': '0.1s' }}>
                {dcPhotos.slice(0, 3).map((ph, i) => (
                  <div key={i} className="prov-dc-photo">
                    <img src={src(ph.url)} alt="" style={{ filter: flt(ph), objectPosition: objPos(ph) }} />
                  </div>
                ))}
              </div>
            )}
          </ProvSection>
          <div className="prov-divider"><FloralWave color={accent} opacity={0.35} /></div>
        </>
      )}

      {/* ══ RSVP ══════════════════════════════════════════════════ */}
      {HAS_RSVP && (
        <>
          <ProvSection
            id="rsvp"
            title={bc.rsvp?.title || 'Подтверждение'}
            subtitle={bc.rsvp?.subtitle}
            accent={accent}
          >
            <div className="prov-reveal" style={{ '--d': '0.1s' }}>
              <RSVPForm project={project} accent={accent} />
            </div>
          </ProvSection>
          <div className="prov-divider"><FloralWave color={accent} opacity={0.35} /></div>
        </>
      )}

      {/* ══ WISHLIST ══════════════════════════════════════════════ */}
      {HAS_WISHLIST && (
        <>
          <ProvSection
            id="wishlist"
            title={bc.wishlist?.title || 'Вишлист'}
            subtitle={bc.wishlist?.subtitle}
            accent={accent}
          >
            <Wishlist project={project} accent={accent} />
          </ProvSection>
          <div className="prov-divider"><FloralWave color={accent} opacity={0.35} /></div>
        </>
      )}

      {/* ══ FOOTER ════════════════════════════════════════════════ */}
      <footer className="prov-footer">
        <FloralWreath accent={accent} rose={rose} />
        <div className="prov-footer-names" style={{ color: accent }}>
          {bride} &amp; {groom}
        </div>
        <div className="prov-footer-date">{fmtDate(weddingDate)}</div>
        <FloralWave color={accent} opacity={0.3} />
        <p className="prov-footer-merci">avec amour</p>
      </footer>

      {/* ══ WATERMARK (free plan) ═════════════════════════════════ */}
      {project.payment_plan === 'free' && (
        <div className="prov-watermark">
          Создано на <a href="/" style={{ color: accent }}>WeddingInvite</a>
        </div>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
